import { useState, useEffect } from 'react';
import { StaffSidebar } from '../../components/StaffSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Edit, Clock, Sparkles, Utensils, SprayCan, Code } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  spa: Sparkles,
  kitchen: Utensils,
  cleaning: SprayCan,
  coding: Code
};

const categoryColors = {
  spa: 'bg-terracotta/10 text-terracotta border-terracotta/20',
  kitchen: 'bg-sage/10 text-sage border-sage/20',
  cleaning: 'bg-golden-hour/20 text-deep-charcoal border-golden-hour/30',
  coding: 'bg-deep-charcoal/10 text-deep-charcoal border-deep-charcoal/20'
};

export default function StaffServices() {
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_minutes: '',
    category: 'spa',
    image_url: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/staff/services`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        duration_minutes: parseInt(formData.duration_minutes)
      };

      if (editingService) {
        await axios.put(
          `${API}/staff/services/${editingService.service_id}`,
          data,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/staff/services`, data, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success('Service created');
      }

      setDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const toggleActive = async (service) => {
    try {
      await axios.put(
        `${API}/staff/services/${service.service_id}`,
        { is_active: !service.is_active },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success(service.is_active ? 'Service deactivated' : 'Service activated');
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      price: String(service.price),
      duration_minutes: String(service.duration_minutes),
      category: service.category,
      image_url: service.image_url || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      duration_minutes: '',
      category: 'spa',
      image_url: ''
    });
  };

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <StaffSidebar />
      
      <main className={`pt-16 lg:pt-0 ${isRTL ? 'lg:mr-64' : 'lg:ml-64'}`}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <div>
              <h1 className="font-heading text-3xl font-bold text-deep-charcoal">
                {t('services')}
              </h1>
              <p className="font-body text-deep-charcoal/60 mt-1">
                {t('manageServices')}
              </p>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-xl bg-terracotta text-white hover:bg-terracotta-dark" data-testid="add-service-btn">
                  <Plus className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('addService')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg bg-white">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingService ? t('updateService') : t('addService')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="font-body text-sm">{t('serviceName')}</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-xl"
                      placeholder="e.g., FTASM Spa - Deep Tissue"
                      required
                      data-testid="service-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-body text-sm">{t('description')}</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-xl"
                      placeholder="Describe the service..."
                      required
                      data-testid="service-desc-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-body text-sm">{t('price')} (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="rounded-xl"
                        placeholder="150.00"
                        required
                        data-testid="service-price-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body text-sm">{t('duration')} (mins)</Label>
                      <Input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                        className="rounded-xl"
                        placeholder="60"
                        required
                        data-testid="service-duration-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-body text-sm">{t('category')}</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                      <SelectTrigger className="rounded-xl" data-testid="service-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spa">{t('spa')}</SelectItem>
                        <SelectItem value="kitchen">{t('kitchen')}</SelectItem>
                        <SelectItem value="cleaning">{t('cleaning')}</SelectItem>
                        <SelectItem value="coding">{t('coding')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-body text-sm">{t('imageUrl')}</Label>
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="rounded-xl"
                      placeholder="https://..."
                      data-testid="service-image-input"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full rounded-xl bg-terracotta text-white hover:bg-terracotta-dark" data-testid="save-service-btn">
                    {editingService ? t('updateService') : t('createService')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {services.map((service, index) => {
                const Icon = categoryIcons[service.category] || Sparkles;
                return (
                  <Card 
                    key={service.service_id}
                    className={`bg-white rounded-xl border-0 shadow-soft overflow-hidden animate-fade-in-up ${!service.is_active ? 'opacity-60' : ''}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`service-card-${service.service_id}`}
                  >
                    {service.image_url && (
                      <div className="h-32 overflow-hidden">
                        <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="p-5">
                      <div className={`flex items-start justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Badge className={`${categoryColors[service.category]} border`}>
                          <Icon className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                          {service.category}
                        </Badge>
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Switch
                            checked={service.is_active}
                            onCheckedChange={() => toggleActive(service)}
                            data-testid={`toggle-${service.service_id}`}
                          />
                        </div>
                      </div>
                      
                      <h3 className="font-heading text-lg font-semibold text-deep-charcoal mb-2">
                        {service.name}
                      </h3>
                      <p className="font-body text-sm text-deep-charcoal/60 mb-4 line-clamp-2">
                        {service.description}
                      </p>
                      
                      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center gap-4 text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <span className="font-body text-sm font-semibold text-terracotta">{formatPrice(service.price)}</span>
                          </div>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Clock className={`w-4 h-4 text-sage ${isRTL ? 'ml-1' : 'mr-1'}`} />
                            <span className="font-body text-sm">{service.duration_minutes}m</span>
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(service)}
                          className="text-deep-charcoal/60 hover:text-terracotta"
                          data-testid={`edit-${service.service_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
